import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsKpiKey,
  CampaignAnalyticsResponse,
  SalesTrafficPeriod,
  SalesTrafficResponse,
} from "@adpilot/shared";
import {
  PERFORMANCE_METRICS,
  type PerformanceKpi,
  type PerformanceTrendPoint,
} from "@/components/shared/performance/performance";

export type DashboardPerformanceMetric = CampaignAnalyticsKpiKey | "totalSales" | "tacos";
export const DEFAULT_DASHBOARD_PERFORMANCE_SLOTS: readonly DashboardPerformanceMetric[] = [
  "totalSales",
  "spend",
  "sales",
  "orders",
  "tacos",
  "acos",
];
export const DASHBOARD_PERFORMANCE_METRICS: readonly DashboardPerformanceMetric[] = [
  "totalSales",
  ...PERFORMANCE_METRICS.slice(0, 3),
  "tacos",
  ...PERFORMANCE_METRICS.slice(3),
];

interface DatePeriod {
  periodStart: string;
  periodEnd: string;
}
interface DashboardPerformanceInput {
  analytics?: CampaignAnalyticsResponse;
  salesTraffic?: SalesTrafficResponse;
  startDate: string;
  endDate: string;
  granularity: CampaignAnalyticsGranularity;
}
const DAY_MS = 86_400_000;
const dateString = (date: Date) => date.toISOString().slice(0, 10);
const dayCount = (start: string, end: string) => (Date.parse(end) - Date.parse(start)) / DAY_MS + 1;

/** Match the Ads read model's clipped calendar weeks (Monday–Sunday) and months. */
function periodsInRange(
  startDate: string,
  endDate: string,
  granularity: CampaignAnalyticsGranularity,
): DatePeriod[] {
  const periods: DatePeriod[] = [];
  let cursor = new Date(`${startDate}T00:00:00Z`);
  const rangeEnd = new Date(`${endDate}T00:00:00Z`);
  while (cursor <= rangeEnd) {
    let end = new Date(cursor);
    if (granularity === "week") end.setUTCDate(end.getUTCDate() + ((7 - end.getUTCDay()) % 7));
    if (granularity === "month")
      end = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0));
    if (end > rangeEnd) end = rangeEnd;
    periods.push({ periodStart: dateString(cursor), periodEnd: dateString(end) });
    cursor = new Date(end.getTime() + DAY_MS);
  }
  return periods;
}

function hasSalesCoverage(period: SalesTrafficPeriod): boolean {
  return (
    period.expectedShopDays > 0 &&
    period.salesCoveredShopDays === period.expectedShopDays &&
    !period.unavailableReasons.includes("scope_incomplete") &&
    period.totalSales !== null &&
    !period.provisionalShopDays
  );
}

function comparableSales(response: SalesTrafficResponse): boolean {
  const { current, previous } = response;
  const days = dayCount(current.startDate, current.endDate);
  return (
    response.currency !== null &&
    response.excludedShops === 0 &&
    hasSalesCoverage(current) &&
    hasSalesCoverage(previous) &&
    !current.stale &&
    !previous.stale &&
    !current.unavailableReasons.includes("currency_mismatch") &&
    !previous.unavailableReasons.includes("currency_mismatch") &&
    days === dayCount(previous.startDate, previous.endDate) &&
    current.expectedShopDays === previous.expectedShopDays &&
    Date.parse(previous.endDate) + DAY_MS === Date.parse(current.startDate)
  );
}

function salesKpi(
  key: "totalSales" | "tacos",
  response: SalesTrafficResponse | undefined,
): PerformanceKpi<DashboardPerformanceMetric> {
  const value = response?.current[key] ?? null;
  const previousValue = response?.previous[key] ?? null;
  const comparable =
    response &&
    comparableSales(response) &&
    (key !== "tacos" ||
      [response.current, response.previous].every(
        (period) =>
          period.tacosCoverage === undefined ||
          period.tacosCoverage?.coveredShopDays === period.expectedShopDays,
      ));
  const change =
    comparable &&
    value !== null &&
    previousValue !== null &&
    (key === "tacos" || previousValue !== 0)
      ? key === "tacos"
        ? value - previousValue
        : (value - previousValue) / Math.abs(previousValue)
      : null;
  return {
    key,
    label: key === "totalSales" ? "Total sales" : "TACOS",
    value,
    previousValue: comparable ? previousValue : null,
    change,
    direction: change === null || change === 0 ? "flat" : change > 0 ? "up" : "down",
    semantics: key === "totalSales" ? "positive-is-good" : "positive-is-bad",
    format: key === "totalSales" ? "currency" : "percent",
    currency: response?.currency ?? null,
    changeKind: key === "tacos" ? "percentage-points" : "relative",
  };
}

function missingAdKpi(key: CampaignAnalyticsKpiKey): PerformanceKpi<DashboardPerformanceMetric> {
  return {
    key,
    label: key,
    value: null,
    previousValue: null,
    change: null,
    direction: "flat",
    semantics: ["spend", "acos", "cpc", "cpa"].includes(key)
      ? "positive-is-bad"
      : ["clicks", "impressions"].includes(key)
        ? "neutral"
        : "positive-is-good",
    format: ["spend", "sales", "cpc", "cpa"].includes(key)
      ? "currency"
      : ["acos", "ctr", "cvr"].includes(key)
        ? "percent"
        : "number",
  };
}

function aggregateSalesBucket(
  period: DatePeriod,
  byDate: ReadonlyMap<string, SalesTrafficResponse["trend"][number]>,
) {
  const days = dayCount(period.periodStart, period.periodEnd);
  const rows = Array.from({ length: days }, (_, i) =>
    byDate.get(dateString(new Date(Date.parse(period.periodStart) + i * DAY_MS))),
  );
  // The API preserves valid partial sums even when another shop is unconnected.
  const availableSalesRows = rows.filter(
    (row) => row && row.totalSales !== null && row.salesCoveredShopDays > 0,
  );
  const completeSales = rows.every((row) => row && hasSalesCoverage(row));
  const totalSales =
    availableSalesRows.length > 0
      ? availableSalesRows.reduce((sum, row) => sum + (row?.totalSales ?? 0), 0)
      : null;
  // A verified zero-sales day still contributes spend to a nonzero weekly/monthly denominator.
  const completeTacos =
    completeSales &&
    rows.every(
      (row) =>
        row &&
        !row.stale &&
        row.spSpend !== null &&
        row.spendCoveredShopDays === row.expectedShopDays &&
        row.unavailableReasons.every((reason) => reason === "zero_sales") &&
        (row.tacos !== null || row.totalSales === 0),
    );
  const legacyTacos =
    completeTacos && totalSales !== null && totalSales > 0
      ? rows.reduce((sum, row) => sum + (row?.spSpend ?? 0), 0) / totalSales
      : null;
  const matchedRows = rows.flatMap((row) => (row?.tacosCoverage ? [row.tacosCoverage] : []));
  const hasMatchedContract = rows.some((row) => row?.tacosCoverage !== undefined);
  const matchedSales = matchedRows.reduce((sum, row) => sum + row.sales, 0);
  const matchedSpend = matchedRows.reduce((sum, row) => sum + row.spSpend, 0);
  const tacos = hasMatchedContract
    ? matchedSales > 0
      ? matchedSpend / matchedSales
      : null
    : legacyTacos;
  const matchedComplete = rows.every(
    (row) =>
      row &&
      !row.provisionalShopDays &&
      row.tacosCoverage?.coveredShopDays === row.expectedShopDays,
  );
  return {
    totalSales,
    tacos,
    metricDataStatus: {
      totalSales: completeSales
        ? ("complete" as const)
        : availableSalesRows.length
          ? ("partial" as const)
          : ("missing" as const),
      tacos: hasMatchedContract
        ? matchedComplete
          ? ("complete" as const)
          : matchedRows.length
            ? ("partial" as const)
            : ("missing" as const)
        : completeTacos
          ? ("complete" as const)
          : ("missing" as const),
    },
  };
}

/** Keep Sales & Traffic independent of Ads availability and preserve each source's null gaps. */
export function buildDashboardPerformance({
  analytics,
  salesTraffic,
  startDate,
  endDate,
  granularity,
}: DashboardPerformanceInput): {
  kpis: PerformanceKpi<DashboardPerformanceMetric>[];
  trend: PerformanceTrendPoint<DashboardPerformanceMetric>[];
} {
  const sales =
    salesTraffic?.current.startDate === startDate && salesTraffic.current.endDate === endDate
      ? salesTraffic
      : undefined;
  const adKpis = new Map(analytics?.kpis.map((kpi) => [kpi.key, kpi]));
  const kpis = DASHBOARD_PERFORMANCE_METRICS.map((key) =>
    key === "totalSales" || key === "tacos"
      ? salesKpi(key, sales)
      : {
          ...(adKpis.get(key) ?? missingAdKpi(key)),
          currency: analytics?.currency ?? null,
        },
  );
  const adTrend = new Map(
    analytics?.trend.map((point) => [`${point.periodStart}/${point.periodEnd}`, point]),
  );
  const salesByDate = new Map(
    sales?.trend
      .filter((row) => row.startDate === row.date && row.endDate === row.date)
      .map((row) => [row.date, row]),
  );
  const trend = periodsInRange(startDate, endDate, granularity).map((period) => {
    const ads = adTrend.get(`${period.periodStart}/${period.periodEnd}`);
    return {
      spend: null,
      sales: null,
      orders: null,
      acos: null,
      cpc: null,
      cpa: null,
      roas: null,
      clicks: null,
      impressions: null,
      ctr: null,
      cvr: null,
      dataStatus: "missing" as const,
      ...ads,
      ...period,
      ...aggregateSalesBucket(period, salesByDate),
    };
  });
  return { kpis, trend };
}

export type DashboardConnection = "ready" | "ads_missing" | "connect";

/**
 * Ads summary and Seller sales load independently. The full connect prompt only
 * replaces the chart once both sources report disconnected; a missing Ads link
 * otherwise stays visible as an inline CTA so Seller-only sales are not hidden.
 */
export function resolveDashboardConnection(
  adsConnected: boolean | undefined,
  sellerConnected: boolean | undefined,
): DashboardConnection {
  if (adsConnected !== false) return "ready";
  return sellerConnected === false ? "connect" : "ads_missing";
}
